/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/print';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/help';
import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/wordcount';
import { Button, SkeletonLoading } from '@saleshandy/design-system';
import { ChevronDown, Pencil } from '@saleshandy/icons';
import { v4 as uuidv4 } from 'uuid';

import {
  AttachmentMetaDataType,
  AttachmentType,
  AttachmentUploadStatus,
  InlineMentionEditorProps,
  MentionOption,
  VisibilityOption,
} from '../../../../shared/editor/types';
import { RootState } from '../../../../store/root-reducer';
import {
  addAlignmentSplitButton,
  addFileToTheLocalAttachments,
  addIcon,
  generateEditorId,
  getAcceptedExtensionsForAttachment,
  setCaretAfterChip,
} from '../../../../shared/editor/helpers';
import DragAndDrop from '../../../../shared/editor/drag-and-drop';
import Attachment from '../../../../shared/components/attachment';
import Alert from '../../../../shared/design-system/components/alert';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  Theme,
} from '../../../../shared/design-system/components/overlay';
import { Images } from '../../../../shared/app-constants';
import ImageIcon from '../../../../shared/components/images/image-icon';
import toaster from '../../../../shared/toaster';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { executeOnRequestStatus } from '../../../../shared/utils';
import { RequestStatus } from '../../../../shared/enums/request-status';
import { removeAIGeneratedVariantDefaultAttachement } from '../../../sequence/sequence-slice';
import { getTaskAssigneeListRequest } from '../../../sequence/extra-actions';
import { insertChipForMentionTag } from './helper';

const InlineMentionEditor = React.forwardRef<
  HTMLDivElement,
  InlineMentionEditorProps
>(
  (
    {
      value,
      onEditorChange,
      error,
      className,
      onEditorBlur,
      onAiColEnrichmentEditorClick,
      disabled = false,
      // mergeTags = [],
      imageUploader,
      onEditorInit,
      getEditorInstance,
      onKeyUp,
      onKeyDown,
      showAddAttachments,
      attachments = [],
      onAttachmentStatusChange,
      onAttachmentsChange,
      sendAttachmentUploadRequest,
      uploadAttachmentRequestStatus,
      uploadAttachmentRequestError,
      uploadedAttachment,
      resetUploadAttachmentRequest,
      errorMessage,
      sendDeleteAttachmentRequest,
      deleteAttachmentRequestStatus,
      deleteAttachmentRequestError,
      deletedAttachmentId,
      resetDeleteAttachmentRequest,
      onAttachmentsMetaChange,
      isTemplateEditorModal,
      sequenceStepVariantId,
      uniqueId,
      action,
      attachmentsClassname = '',
      placeholder = '',
      hideHeader = false,
      isInline = true,
      onPaste,
      showToolBarContainer = false,
      showFooterToolbarContainer = false,
      showFooterVisibilitySelector = false,
      showFooterSaveButton = false,
      showFooterCancelButton = false,
      onFooterSave,
      onFooterCancel,
      onDiscard,
      onEditClick,
      saveButtonText,
      cancelButtonText,
      isFooterSaveButtonDisabled = false,
      isFooterCancelButtonDisabled = false,
      isFooterDiscardButtonDisabled = false,
      selectedVisibilityKey,
      onVisibilityChange,
      visibilityOptions,
      showSkeleton = false,
      autoFocus = false,
      attachmentMsg = false,
      notesTabAttachment = false,
      updateNoteRequestStatus,
      createNoteRequestStatus,
      showDiscardButton = false,
      discardButtonText,
      blockedDraftText,
      showEditIcon = false,
      showDraftStatus = false,
      showDraftSavedMessage,
      setShowDraftSavedMessage,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const {
      isAiGeneratedVariant,
      aiGeneratedVariantDetails: { sequenceStepVariantAttachments },
    } = useSelector((state: RootState) => state.sequence);
    const getMembersList = useSelector(
      (state: RootState) => state?.sequence?.taskAssigneeList,
    );
    const getMembersListRequestStatus = useSelector(
      (state: RootState) => state?.sequence?.getTaskAssigneeListRequest?.status,
    );

    const [id] = useState(generateEditorId());
    const editorInstanceRef = useRef<any>(null);
    const placeholderRef = useRef(placeholder);
    const attachmentInput = useRef<HTMLInputElement>(null);
    const dropRef = useRef<HTMLDivElement>(null);
    const [errors, setErrors] = useState<string>('');

    const [dragging, setDragging] = useState(false);
    const [attachmentsMeta, setAttachmentsMeta] = useState<
      AttachmentMetaDataType[]
    >([]);
    const [hasEditorInitialized, setHasEditorInitialized] = useState(false);
    const [showVisibilityOptions, setShowVisibilityOptions] = useState(false);
    const mergeTags = getMembersList
      ?.filter(
        (item) =>
          item?.firstName?.trim() !== '' && item?.lastName?.trim() !== '',
      )
      ?.map((item) => ({
        key: `${item?.id}`,
        label: `${item?.firstName} ${item?.lastName}`,
        tag: `${item?.firstName} ${item?.lastName}`,
      }));

    const resolvedVisibilityOptions = useMemo<VisibilityOption[]>(
      () =>
        visibilityOptions?.length
          ? visibilityOptions
          : [
              {
                key: 'private',
                label: 'Private Note',
                header: 'Private',
                description: 'Visible only to you and people tagged here',
                icon: Images.LockGolden,
              },
              {
                key: 'public',
                label: 'Public Note',
                header: 'Public',
                description: 'Visible to everyone in the organisation',
                icon: Images.LockOpenGreen,
              },
            ],
      [visibilityOptions],
    );
    const isViewMode =
      !showFooterToolbarContainer &&
      !showFooterVisibilitySelector &&
      !showFooterSaveButton;

    const [
      selectedVisibility,
      setSelectedVisibility,
    ] = useState<VisibilityOption>(resolvedVisibilityOptions[0]);

    useEffect(() => {
      placeholderRef.current = placeholder;
    }, [placeholder]);

    const syncPlaceholderToEditor = useCallback((editor: any) => {
      if (!editor?.getBody) return;
      const body = editor.getBody();
      const text = editor.getContent({ format: 'text' }).trim();
      const showPlaceholder = text === '';
      const placeholderValue = placeholderRef.current ?? '';
      editor.dom.setAttrib(
        body,
        'data-mce-placeholder',
        showPlaceholder ? placeholderValue : null,
      );
      editor.dom.setAttrib(
        body,
        'aria-placeholder',
        showPlaceholder ? placeholderValue : null,
      );
      editor.settings.placeholder = placeholderValue;
    }, []);

    useEffect(() => {
      const editor = editorInstanceRef.current;
      if (editor && hasEditorInitialized) {
        syncPlaceholderToEditor(editor);
      }
    }, [placeholder, hasEditorInitialized, syncPlaceholderToEditor]);

    useEffect(() => {
      // Only update if the prop actually changed and differs from current state
      // This prevents overriding user selections before parent state updates
      if (
        selectedVisibilityKey &&
        selectedVisibility?.key !== selectedVisibilityKey
      ) {
        const defaultVisibility =
          resolvedVisibilityOptions.find(
            (option) => option.key === selectedVisibilityKey,
          ) || resolvedVisibilityOptions[0];
        if (defaultVisibility.key !== selectedVisibility?.key) {
          setSelectedVisibility(defaultVisibility);
        }
      }
    }, [resolvedVisibilityOptions, selectedVisibilityKey]);

    // Handle update note request status to show "Saving draft" / "Draft Saved" messages
    useEffect(() => {
      if (!showDraftStatus || blockedDraftText || value?.length === 0) return;
      const isNotPending =
        updateNoteRequestStatus === RequestStatus.Succeeded ||
        updateNoteRequestStatus === RequestStatus.Failed ||
        createNoteRequestStatus === RequestStatus.Succeeded ||
        createNoteRequestStatus === RequestStatus.Failed;

      if (isNotPending) {
        setShowDraftSavedMessage(true);
      }
      const timer = setTimeout(() => {
        setShowDraftSavedMessage(false);
      }, 2000);
      // eslint-disable-next-line consistent-return
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [
      showDraftStatus,
      updateNoteRequestStatus,
      createNoteRequestStatus,
      blockedDraftText,
    ]);

    const editorClass = classNames([
      `inline-mention-editor ${
        isViewMode ? 'inline-mention-view-mode-task' : ''
      }`,
      'editor',
      className,
      { error },
    ]);

    const mentionOptions = useMemo<MentionOption[]>(
      () =>
        mergeTags?.map((tag) => ({
          id: `${tag.key}`,
          label: tag.label,
          value: tag.tag?.replace(/^@/, '') || '',
        })) || [],
      [mergeTags],
    );

    const toolbarContainerClassName = classNames('toolbar-container', {
      'hide-toolbar-container': !showToolBarContainer,
    });

    const onUploadProgress = (
      e,
      updatedAttachmentsMeta: AttachmentMetaDataType[],
      attachmentIdentifier: string,
    ) => {
      let attachmentMeta = updatedAttachmentsMeta?.find(
        (item) => item?.attachmentIdentifier === attachmentIdentifier,
      );

      if (attachmentMeta) {
        attachmentMeta = {
          ...attachmentMeta,
          percentage: Math.round((100 * e.loaded) / e.total),
        };

        setAttachmentsMeta(
          updatedAttachmentsMeta?.map((item) =>
            item.attachmentIdentifier === attachmentIdentifier
              ? attachmentMeta
              : item,
          ),
        );
      }
    };

    const handleAttachments = (files: FileList | File[]) => {
      const localAttachments = attachments?.length ? [...attachments] : [];
      const onLocalAttachmentChange = (obj) => {
        localAttachments.push(obj);
      };

      const localAttachmentsMeta = [...attachmentsMeta];
      const onLocalAttachmentMetaChange = (obj) => {
        localAttachmentsMeta.push(obj);
      };

      let shouldBreakLoop = false;
      const breakLoop = () => {
        shouldBreakLoop = true;
      };

      const filesArr = Array.isArray(files) ? files : Array.from(files || []);

      if (filesArr?.length) {
        filesArr.forEach((file) => {
          if (shouldBreakLoop) {
            return;
          }

          addFileToTheLocalAttachments({
            file,
            localAttachments,
            onLocalAttachmentChange,
            onLocalAttachmentMetaChange,
            breakLoop,
            t,
            content: value,
            attachmentMsg,
          });
        });

        setAttachmentsMeta(localAttachmentsMeta);

        localAttachments.forEach((attachment, indx) => {
          if (
            attachment.status === AttachmentUploadStatus.Upload &&
            sendAttachmentUploadRequest
          ) {
            if (hideHeader) {
              localAttachments[indx].status =
                AttachmentUploadStatus.UploadSuccess;
              return;
            }

            const formData = new FormData();
            formData.append('file', attachment.file);
            formData.append(
              'attachmentIdentifier',
              attachment.attachmentIdentifier,
            );
            formData.append('attachmentSize', attachment.file.size);
            if (notesTabAttachment) {
              formData.append('noteAttachment', 'true');
            }

            const attachmentMeta = localAttachmentsMeta.find(
              (item) =>
                item.attachmentIdentifier === attachment.attachmentIdentifier,
            );

            sendAttachmentUploadRequest(
              {
                formData,
                onUploadProgress: (e) =>
                  onUploadProgress(
                    e,
                    localAttachmentsMeta,
                    attachment.attachmentIdentifier,
                  ),
                cancelToken: attachmentMeta?.source?.token,
              },
              uniqueId,
            );

            localAttachments[indx].status = AttachmentUploadStatus.Uploading;
          }
        });

        onAttachmentsChange?.(localAttachments);
      }
    };

    const validateMentions = (
      htmlString: string,
      membersList: { id: number; firstName: string; lastName: string }[],
    ) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const mentionNodes = doc.querySelectorAll('.inline-mention-text');

      // eslint-disable-next-line no-restricted-syntax
      for (const node of Array.from(mentionNodes)) {
        const mentionId = Number(node.getAttribute('data-mention-id'));
        const mentionText = node.textContent?.trim() || ''; // e.g. "@Test Member"

        // Find member by ID
        const member = membersList?.find((m) => m?.id === mentionId);
        if (!member) {
          return { valid: false, error: `Mentioned member is not found` };
        }

        // Build expected mention text
        const expectedText = `@${member.firstName} ${member.lastName}`.trim();

        if (mentionText !== expectedText) {
          return {
            valid: false,
            error: 'A mention does not match a valid user.',
          };
        }
      }

      return { valid: true };
    };

    const handleOnFooterSave = useCallback(() => {
      const result = validateMentions(value, getMembersList);
      setErrors(result?.error);
      if (result?.valid) {
        onFooterSave();
      }
    }, [value, getMembersList, validateMentions]);

    const handleFileInput = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!showAddAttachments) {
        return;
      }

      const { files } = event.target;
      handleAttachments(files);
      if (attachmentInput.current) {
        attachmentInput.current.value = '';
      }
    };
    // "View mode" = no footer controls are rendered (pencil icon visible).
    // In this mode we want content to be non-editable and we should not show toolbar in header.

    const shouldRenderHeaderToolbarContainer =
      !hideHeader && !showFooterToolbarContainer;
    const shouldRenderFooterToolbarContainer = showFooterToolbarContainer;
    const shouldRenderToolbarContainer =
      shouldRenderHeaderToolbarContainer || shouldRenderFooterToolbarContainer;
    const toolbarContainerId = `toolbar-container-${id}`;
    const renderAttachmentInput = () => {
      if (!showAddAttachments) {
        return null;
      }

      return (
        <input
          ref={attachmentInput}
          type="file"
          hidden
          accept={getAcceptedExtensionsForAttachment()}
          onChange={handleFileInput}
        />
      );
    };
    const shouldRenderFooterSection =
      shouldRenderFooterToolbarContainer ||
      showFooterVisibilitySelector ||
      showFooterSaveButton ||
      showFooterCancelButton;
    const resolvedSaveButtonText =
      saveButtonText || t('common.save', { defaultValue: 'Save' });
    const resolvedCancelButtonText =
      cancelButtonText || t('common.cancel', { defaultValue: 'Cancel' });
    const resolvedDiscardButtonText =
      discardButtonText || t('common.discard', { defaultValue: 'Discard' });

    const handleVisibilitySelection = (option: VisibilityOption) => {
      setSelectedVisibility(option);
      setShowVisibilityOptions(false);
      onVisibilityChange?.(option.key);
    };

    const handleVisibilitySelectorKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
    ) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setShowVisibilityOptions((prevVisibility) => !prevVisibility);
      }
    };

    const getInitials = (label: string) => {
      if (!label) return '';
      const parts = label?.trim()?.split(/\s+/);
      const first = parts[0]?.charAt(0) ?? '';
      const second = parts[1]?.charAt(0) ?? '';
      return `${first}${second}`?.toUpperCase();
    };

    const registerMentionAutocompleter = useCallback(() => {
      const editor = editorInstanceRef.current;
      if (!editor?.ui?.registry) return;

      if (editor.ui.registry.removeAutocompleter) {
        editor.ui.registry.removeAutocompleter('inline-mentions');
      }

      editor.ui.registry.addAutocompleter('inline-mentions', {
        ch: '@',
        minChars: 0,
        columns: 1,
        highlightOn: ['label'],
        matches: (rng, text: string, pattern: string) => {
          if (text.slice(-1) === '@' || pattern !== '') {
            return true;
          }
          return false;
        },
        fetch: (pattern: string) =>
          new Promise((resolve) => {
            const query = pattern?.toLowerCase?.() || '';
            const filtered = query
              ? mentionOptions.filter(
                  (option) =>
                    option.label.toLowerCase().includes(query) ||
                    option.value.toLowerCase().includes(query),
                )
              : mentionOptions;

            resolve(
              filtered?.map((option) => ({
                type: 'cardmenuitem',
                value: option.id,
                items: [
                  {
                    type: 'cardcontainer',
                    direction: 'horizontal',
                    classes: ['inline-mention-option'],
                    items: [
                      {
                        type: 'cardtext',
                        text: getInitials(option?.label),
                        classes: ['inline-mention-option__initials'],
                      },
                      {
                        type: 'cardtext',
                        text: option.label,
                        name: 'label',
                        classes: ['inline-mention-option__label'],
                      },
                    ],
                  },
                ],
              })),
            );
          }),
        onAction: (api, rng, mentionId) => {
          const uuid = uuidv4();
          const option = mentionOptions?.find((item) => item?.id === mentionId);
          if (!option) {
            api.hide();
            return;
          }

          const editorInstance = editorInstanceRef?.current;
          if (!editorInstance) return;

          editorInstance?.selection?.setRng(rng);
          const mentionText = option.value.startsWith('@')
            ? option.value
            : `@${option.value}`;
          const mentionHtml = `<span class="inline-mention-text" data-mention-id="${option.id}">${mentionText}</span>`;
          editorInstance.insertContent(
            insertChipForMentionTag(mentionHtml, uuid, false),
          );
          setCaretAfterChip(editorInstance, uuid);
          api.hide();
        },
      });
    }, [mentionOptions]);

    useEffect(() => {
      if (hasEditorInitialized) {
        registerMentionAutocompleter();
      }
    }, [hasEditorInitialized, registerMentionAutocompleter]);

    useEffect(() => {
      // Only fetch if members list doesn't exist and no request is already pending
      if (
        !getMembersList?.length &&
        getMembersListRequestStatus !== RequestStatus.Pending
      ) {
        dispatch(getTaskAssigneeListRequest({ includeAllAccountUsers: true }));
      }
    }, [dispatch, getMembersList, getMembersListRequestStatus]);

    useEffect(
      () => () => {
        if (showAddAttachments) {
          setDragging(false);
          setAttachmentsMeta([]);
          resetUploadAttachmentRequest?.(uniqueId);
          resetDeleteAttachmentRequest?.(uniqueId);
        }
      },
      [
        showAddAttachments,
        resetUploadAttachmentRequest,
        resetDeleteAttachmentRequest,
        uniqueId,
      ],
    );

    useEffect(
      () => () => {
        if (editorInstanceRef.current?.ui?.registry?.removeAutocompleter) {
          editorInstanceRef.current.ui.registry.removeAutocompleter(
            'inline-mentions',
          );
        }
      },
      [],
    );

    useEffect(() => {
      if (showAddAttachments) {
        onAttachmentsMetaChange?.(attachmentsMeta);
      }
    }, [showAddAttachments, attachmentsMeta, onAttachmentsMetaChange]);

    useEffect(() => {
      if (showAddAttachments && attachments?.length) {
        const editorContainer = document.getElementById(
          'editor-editable-content-container',
        );
        if (editorContainer) {
          // Only scroll if there's actual content or if the container has scrollable content
          const hasContent =
            value?.trim() && value !== '<div></div>' && value !== '<p></p>';
          // Only scroll if there's content, otherwise let attachments render naturally
          if (hasContent) {
            editorContainer.scrollTop = editorContainer.scrollHeight;
          }
        }
      }
    }, [showAddAttachments, attachments, value]);

    useEffect(() => {
      executeOnRequestStatus({
        status: uploadAttachmentRequestStatus,
        onSuccess: () => {
          const attachment = attachments.find(
            (item) =>
              item.attachmentIdentifier ===
              uploadedAttachment?.attachmentIdentifier,
          );

          if (attachment && uploadedAttachment) {
            onAttachmentStatusChange?.({
              ...attachment,
              ...uploadedAttachment,
              status: AttachmentUploadStatus.UploadSuccess,
            });
          }

          resetUploadAttachmentRequest?.(uniqueId);
        },
        onFailed: () => {
          if (uploadAttachmentRequestError) {
            toaster.error(uploadAttachmentRequestError.message, {
              theme: Theme.New,
            });
          }
        },
      });
    }, [
      uploadAttachmentRequestStatus,
      uploadAttachmentRequestError,
      uploadedAttachment,
      attachments,
      onAttachmentStatusChange,
      resetUploadAttachmentRequest,
      uniqueId,
    ]);

    useEffect(() => {
      executeOnRequestStatus({
        status: deleteAttachmentRequestStatus,
        onSuccess: () => {
          const attachmentIdToDelete = deletedAttachmentId;
          const updatedAttachments = attachments.filter(
            (attachment) => attachment.attachmentId !== attachmentIdToDelete,
          );

          onAttachmentsChange?.(updatedAttachments, true);

          resetDeleteAttachmentRequest?.(uniqueId);
        },
        onFailed: () => {
          if (deleteAttachmentRequestError) {
            toaster.error(deleteAttachmentRequestError.message, {
              theme: Theme.New,
            });
          }
          resetDeleteAttachmentRequest?.(uniqueId);
        },
      });
    }, [
      deleteAttachmentRequestStatus,
      deletedAttachmentId,
      deleteAttachmentRequestError,
      attachments,
      onAttachmentsChange,
      resetDeleteAttachmentRequest,
      uniqueId,
    ]);

    const handleAttachment = () => {
      if (!showAddAttachments) {
        return;
      }

      attachmentInput?.current?.click();
    };

    const handleDroppedFiles = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!showAddAttachments || hideHeader) {
        return;
      }

      setDragging(false);
      const { files } = event.dataTransfer;
      handleAttachments(files);
      event.dataTransfer.clearData();
    };

    const handleAttachmentDelete = (
      attachmentIdentifier: string,
      attachmentId: number,
    ) => {
      let attachment: AttachmentType | null = null;

      if (
        isAiGeneratedVariant &&
        sequenceStepVariantAttachments?.length > 0 &&
        attachmentId
      ) {
        dispatch(removeAIGeneratedVariantDefaultAttachement(attachmentId));
      }

      if (attachmentIdentifier) {
        attachment =
          attachments.find(
            (item) => item.attachmentIdentifier === attachmentIdentifier,
          ) || null;
      } else if (attachmentId) {
        attachment =
          attachments.find((item) => item.attachmentId === attachmentId) ||
          null;
      }

      if (attachment) {
        if (action === 'forward') {
          onAttachmentsChange?.(
            attachments.filter(
              (item) => item.attachmentIdentifier !== attachmentIdentifier,
            ),
            true,
          );
          return;
        }

        if (attachment.status === AttachmentUploadStatus.Uploading) {
          const attachmentMeta = attachmentsMeta.find(
            (item) =>
              item.attachmentIdentifier === attachment.attachmentIdentifier,
          );

          attachmentMeta?.source?.cancel('Upload has been cancelled.');

          onAttachmentsChange?.(
            attachments.filter(
              (item) => item.attachmentIdentifier !== attachmentIdentifier,
            ),
            true,
          );

          return;
        }

        attachment.status = AttachmentUploadStatus.Deleting;
        onAttachmentStatusChange?.(attachment);
      }

      sendDeleteAttachmentRequest?.(
        {
          attachmentId,
          deleteFromTemplate: isTemplateEditorModal,
          ...(sequenceStepVariantId && { sequenceStepVariantId }),
        },
        uniqueId,
      );
    };

    const handleAttachmentReupload = (_attachment: AttachmentType) => {
      const attachment = { ..._attachment };

      if (
        attachment.status === AttachmentUploadStatus.UploadFailed &&
        sendAttachmentUploadRequest
      ) {
        const formData = new FormData();
        formData.append('file', attachment.file);
        formData.append(
          'attachmentIdentifier',
          attachment.attachmentIdentifier,
        );
        formData.append('attachmentSize', attachment.file.size);
        if (notesTabAttachment) {
          formData.append('noteAttachment', 'true');
        }

        const source = axios.CancelToken.source();

        sendAttachmentUploadRequest(
          {
            formData,
            onUploadProgress: (e) =>
              onUploadProgress(
                e,
                attachmentsMeta,
                attachment.attachmentIdentifier,
              ),
            cancelToken: source.token,
          },
          uniqueId,
        );

        setAttachmentsMeta(
          attachmentsMeta.map((item) =>
            item.attachmentIdentifier === attachment.attachmentIdentifier
              ? { ...item, percentage: 0, source }
              : item,
          ),
        );

        attachment.status = AttachmentUploadStatus.Uploading;
        onAttachmentStatusChange?.(attachment);
      }
    };

    const getToolbarConfig = () => {
      if (hideHeader && !showFooterToolbarContainer) {
        return '';
      }

      const toolbarSections = [
        `${
          showAddAttachments && 'attachment'
        } bold italic underline forecolor numlist bullist`,
        ['link', 'image', 'code'].filter(Boolean).join(' '),
        'unlink',
      ];

      return toolbarSections
        .map((section) => section.trim())
        .filter(Boolean)
        .join(' | ');
    };

    const toolbarConfig = getToolbarConfig();

    return (
      <div className={editorClass} ref={ref}>
        {shouldRenderHeaderToolbarContainer && (
          <div className={toolbarContainerClassName}>
            <div className="d-flex align-items-center w-100">
              <div
                id={toolbarContainerId}
                className="fixed-toolbar-container"
              />
              <div className="toolbar-extra-btn" />
            </div>
            {renderAttachmentInput()}
          </div>
        )}

        {!shouldRenderHeaderToolbarContainer && renderAttachmentInput()}

        <div
          id="editor-editable-content-container"
          className="editor-editable-content-container"
          ref={dropRef}
          onDrop={isViewMode ? undefined : handleDroppedFiles}
        >
          <div className="editor-editable-content-wrapper">
            {showSkeleton ? (
              <div
                className="d-flex flex-column gap-8"
                style={{ padding: '12px' }}
              >
                <SkeletonLoading width={67} height={18} />
                <SkeletonLoading width={240} height={14} />
              </div>
            ) : (
              <DragAndDrop
                dropRef={dropRef}
                dragging={dragging}
                setDragging={setDragging}
                showAddAttachments={showAddAttachments}
                hideHeader={hideHeader}
              >
                <TinyMCEEditor
                  // Remount editor when toolbar container location changes.
                  // TinyMCE reads `fixed_toolbar_container` only on init, so switching
                  // from header -> footer (edit mode) needs a re-init to render toolbar.
                  key={`Editor-${disabled ? 'disabled' : 'enabled'}-${
                    shouldRenderFooterToolbarContainer ? 'footer' : 'header'
                  }`}
                  disabled={disabled}
                  id={`editor-${id}`}
                  value={value}
                  onEditorChange={(updatedValue: string) => {
                    onEditorChange(updatedValue);
                  }}
                  onKeyUp={(event) => {
                    onKeyUp?.(event);
                  }}
                  onKeyDown={(event) => {
                    onKeyDown?.(event as any);
                  }}
                  onBlur={() => {
                    onEditorBlur?.();
                  }}
                  init={{
                    forced_root_block: 'div',
                    menubar: false,
                    placeholder,
                    auto_focus: autoFocus,
                    inline_styles: true,
                    toolbar: toolbarConfig,
                    plugins: [
                      'advlist autolink lists charmap preview',
                      'searchreplace visualblocks fullscreen',
                      'table paste help wordcount',
                    ],
                    inline: isInline,
                    ...(shouldRenderToolbarContainer
                      ? { fixed_toolbar_container: `#${toolbarContainerId}` }
                      : {}),
                    toolbar_persist: true,
                    toolbar_location: 'top',
                    browser_spellcheck: true,
                    contextmenu: false,
                    skin_url: '/assets/libs/tinymce/skins/ui/sh',
                    setup: (editor) => {
                      editorInstanceRef.current = editor;
                      editor.on('mousedown', () => {
                        onAiColEnrichmentEditorClick?.();
                      });
                      editor.on('init', () => {
                        setHasEditorInitialized(true);
                        onEditorInit?.();
                        getEditorInstance?.(editor);
                        syncPlaceholderToEditor(editor);
                        editor.on('change SetContent ExecCommand', () =>
                          syncPlaceholderToEditor(editor),
                        );
                        editor.on('paste', () => {
                          setTimeout(() => syncPlaceholderToEditor(editor), 0);
                        });

                        // Move cursor to end when autoFocus is enabled
                        if (autoFocus) {
                          setTimeout(() => {
                            try {
                              const body = editor.getBody();
                              if (body) {
                                const range = editor.selection.getRng();
                                range.selectNodeContents(body);
                                range.collapse(false); // false = collapse to end
                                editor.selection.setRng(range);
                              }
                            } catch (e) {
                              // Silently fail if cursor positioning fails
                            }
                          }, 0);
                        }
                      });
                      addIcon(editor);
                      addAlignmentSplitButton(editor);
                      editor.ui.registry.addButton('attachment', {
                        icon: 'attachment',
                        tooltip: 'Insert Attachment',
                        onAction: handleAttachment,
                      });
                    },
                    file_picker_callback: (cb) => {
                      const input = document.createElement('input');
                      input.setAttribute('type', 'file');
                      input.setAttribute('accept', 'image/*');
                      input.onchange = (e: any) => {
                        const file = e.target.files[0];
                        const reader = new FileReader();
                        reader.onload = () => {
                          const fileId = file.name;
                          const { blobCache } =
                            editorInstanceRef?.current?.editorUpload || {};
                          const base64 = reader.result
                            ?.toString()
                            ?.split(',')[1];
                          if (!blobCache || !base64) return;
                          const blobInfo = blobCache.create(
                            fileId,
                            file,
                            base64,
                          );
                          blobCache.add(blobInfo);
                          cb(blobInfo.blobUri(), { title: file.name });
                        };
                        reader.readAsDataURL(file);
                      };

                      input.click();
                    },
                    images_upload_handler: (
                      blobInfo,
                      success,
                      failure,
                      progress,
                    ) => {
                      if (!imageUploader) {
                        failure('Image upload handler is not provided.', {
                          remove: true,
                        });
                        return;
                      }

                      const formData = new FormData();
                      formData.append(
                        'file',
                        blobInfo.blob(),
                        blobInfo.filename(),
                      );
                      imageUploader({
                        formData,
                        onUploadProgress: (e) => {
                          progress((e.loaded / e.total) * 100);
                        },
                      }).then((response: any) => {
                        if (response.payload?.error) {
                          failure('Error occurred while uploading the image.', {
                            remove: true,
                          });
                        } else {
                          success(response.payload?.payload?.location);
                        }
                      });
                    },
                    paste_preprocess: (plugin, args) => {
                      if (!onPaste) {
                        return;
                      }

                      onPaste(args.content);
                    },
                  }}
                />

                <div id={id} className="editor-editable-content" />

                {showAddAttachments && attachments?.length > 0 && (
                  <div
                    id="attachments-list"
                    className={`attachments-list ${attachmentsClassname}`}
                  >
                    {attachments.map((attachment) => (
                      <Attachment
                        key={attachment.attachmentIdentifier}
                        attachment={attachment}
                        attachmentMeta={attachmentsMeta.find(
                          (item) =>
                            item.attachmentIdentifier ===
                            attachment.attachmentIdentifier,
                        )}
                        handleAttachmentDelete={handleAttachmentDelete}
                        handleAttachmentReupload={handleAttachmentReupload}
                      />
                    ))}
                  </div>
                )}
              </DragAndDrop>
            )}
            {!shouldRenderFooterSection && showEditIcon && (
              <span
                className="editor-editable-content-edit cursor-pointer"
                {...accessibleOnClick(() => onEditClick?.())}
              >
                <OverlayTooltip text="Edit Note">
                  <Pencil width={16} height={16} />
                </OverlayTooltip>
              </span>
            )}
          </div>
        </div>

        {shouldRenderFooterSection && (
          <div className="upcoming-task-footer">
            {shouldRenderFooterToolbarContainer && (
              <div className={toolbarContainerClassName}>
                <div className="d-flex align-items-center w-100">
                  <div
                    id={toolbarContainerId}
                    className="fixed-toolbar-container"
                  />
                </div>
              </div>
            )}

            <div className="visibility-selector-container">
              {showSkeleton ? (
                <div className="d-flex align-items-center gap-8">
                  <SkeletonLoading
                    width={32}
                    height={32}
                    style={{ borderRadius: '4px' }}
                  />
                  <SkeletonLoading
                    width={32}
                    height={32}
                    style={{ borderRadius: '4px' }}
                  />
                  <SkeletonLoading
                    width={82}
                    height={32}
                    style={{ borderRadius: '4px' }}
                  />
                </div>
              ) : (
                <>
                  {showDraftStatus &&
                    ((updateNoteRequestStatus === RequestStatus.Pending ||
                      createNoteRequestStatus === RequestStatus.Pending) &&
                    value?.length > 0 &&
                    !blockedDraftText ? (
                      <div className="notes-draft-saved">
                        <span className="notes-draft-saved-text">
                          Saving draft
                        </span>
                      </div>
                    ) : showDraftSavedMessage ? (
                      <div className="notes-draft-saved">
                        <span className="notes-draft-saved-text">
                          Draft Saved
                        </span>
                      </div>
                    ) : null)}
                  {showFooterVisibilitySelector && selectedVisibility && (
                    <div className="visibility-selector">
                      <OverlayPopover
                        show={showVisibilityOptions}
                        rootClose
                        placement={Placement.BottomEnd}
                        onToggle={(next) => setShowVisibilityOptions(!!next)}
                        className="toolbar-container-popover"
                        content={
                          <div className="visibility-selector__menu">
                            {resolvedVisibilityOptions?.map((option) => (
                              <div
                                key={option.key}
                                className={`visibility-selector__option ${
                                  selectedVisibility.key === option.key
                                    ? 'selected'
                                    : ''
                                }`}
                                {...accessibleOnClick(() => {
                                  handleVisibilitySelection(option);
                                })}
                              >
                                <ImageIcon
                                  src={option.icon}
                                  alt={`${option.label} visibility`}
                                />
                                <div className="visibility-selector__option-texts">
                                  <span>{option.label}</span>
                                  <span>{option.description}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <div
                          className="visibility-selector__current"
                          role="button"
                          tabIndex={0}
                          onKeyDown={handleVisibilitySelectorKeyDown}
                        >
                          <ImageIcon
                            src={selectedVisibility.icon}
                            alt={`${selectedVisibility.label} visibility`}
                          />
                          <span>{selectedVisibility.header}</span>
                          <span
                            className={`visibility-selector__chevron ${
                              showVisibilityOptions ? 'open' : ''
                            }`}
                          >
                            <ChevronDown />
                          </span>
                        </div>
                      </OverlayPopover>
                    </div>
                  )}

                  {showDiscardButton && (
                    <Button
                      onClick={onDiscard}
                      disabled={isFooterDiscardButtonDisabled}
                      className="footer-cancel-btn"
                    >
                      {resolvedDiscardButtonText}
                    </Button>
                  )}

                  {showFooterCancelButton && (
                    <Button
                      onClick={onFooterCancel}
                      disabled={isFooterCancelButtonDisabled}
                      className="footer-cancel-btn"
                    >
                      {resolvedCancelButtonText}
                    </Button>
                  )}

                  {showFooterSaveButton && (
                    <Button
                      onClick={handleOnFooterSave}
                      disabled={isFooterSaveButtonDisabled || !!errors}
                    >
                      {resolvedSaveButtonText}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {(error || errors) && (
          <Alert
            contentContainer={<span>{errorMessage || errors}</span>}
            variant={Alert.Variant.Danger}
            theme={Alert.Theme.New}
            className="email-content-error"
            svgIcon={Images.AlertTriangleRed}
          />
        )}
      </div>
    );
  },
);

InlineMentionEditor.displayName = 'InlineMentionEditor';
InlineMentionEditor.defaultProps = {
  error: false,
  disabled: false,
};

export default memo(InlineMentionEditor);
